import { Component } from '@theme/component';

const PANEL_HEIGHT_DELAY = 360;

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<TabsRefs>} */
class TabsComponent extends Component {
    requiredRefs = ['panels'];

    /** @type {number | undefined} */
    heightTimer;

    connectedCallback() {
        super.connectedCallback();

        this.syncTabLabels();
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.activateTab(this.activeIndex, { animate: false });
    }

    updatedCallback() {
        super.updatedCallback();

        this.syncTabLabels();
        this.activateTab(this.activeIndex, { animate: false });
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
    }

    get tabs() {
        return Array.from(this.querySelectorAll('[data-tabs-tab]'));
    }

    get activeIndex() {
        const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

        return Math.max(0, selectedIndex);
    }

    syncTabLabels() {
        for (const [index, panel] of this.refs.panels.entries()) {
            const label = panel.dataset.tabLabel;
            const tab = this.tabs[index];

            if (!label || !(tab instanceof HTMLElement)) continue;

            tab.textContent = label;
        }
    }

    /**
     * @param {MouseEvent} event
     */
    handleClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const tab = target.closest('[data-tabs-tab]');
        if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

        const index = this.tabs.indexOf(tab);
        if (index === -1) return;

        this.activateTab(index);
    };

    /**
     * @param {KeyboardEvent} event
     */
    handleKeydown = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

        const tabs = this.tabs;
        const currentIndex = tabs.indexOf(target);
        if (currentIndex === -1) return;

        const lastIndex = tabs.length - 1;
        const keyMap = {
            ArrowLeft: currentIndex - 1,
            ArrowRight: currentIndex + 1,
            Home: 0,
            End: lastIndex,
        };

        if (!(event.key in keyMap)) return;

        event.preventDefault();

        const nextIndex = (keyMap[event.key] + tabs.length) % tabs.length;
        this.activateTab(nextIndex);
        tabs[nextIndex]?.focus();
    };

    /**
     * @param {number} selectedIndex
     * @param {{ animate?: boolean }} [options]
     */
    activateTab(selectedIndex, options = {}) {
        const shouldAnimate = options.animate !== false;
        const activePanel = this.refs.panels.find((panel) => panel.hasAttribute('data-tabs-active'));
        const panelHost = activePanel?.parentElement ?? this.refs.panels[0]?.parentElement;
        const startHeight = panelHost?.getBoundingClientRect().height ?? 0;

        this.style.setProperty('--active-tab-index', String(selectedIndex));
        this.style.setProperty('--tabs-count', String(this.tabs.length));

        for (const [index, tab] of this.tabs.entries()) {
            const isActive = index === selectedIndex;

            tab.setAttribute('aria-selected', String(isActive));
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
        }

        for (const [index, panel] of this.refs.panels.entries()) {
            const isActive = index === selectedIndex;

            if (isActive) {
                panel.hidden = false;
                panel.removeAttribute('inert');
                panel.getBoundingClientRect();

                if (shouldAnimate) {
                    window.requestAnimationFrame(() => {
                        panel.toggleAttribute('data-tabs-active', true);
                    });
                } else {
                    panel.toggleAttribute('data-tabs-active', true);
                }
            } else {
                panel.removeAttribute('data-tabs-active');
                panel.toggleAttribute('inert', true);
                panel.hidden = true;
            }

            panel.setAttribute('tabindex', isActive ? '0' : '-1');
        }

        if (shouldAnimate && panelHost instanceof HTMLElement) {
            this.animatePanelHeight(panelHost, startHeight);
        }
    }

    /**
     * @param {HTMLElement} panelHost
     * @param {number} startHeight
     */
    animatePanelHeight(panelHost, startHeight) {
        if (this.heightTimer) window.clearTimeout(this.heightTimer);

        const endHeight = panelHost.getBoundingClientRect().height;

        panelHost.style.overflow = 'hidden';
        panelHost.style.height = `${startHeight}px`;
        panelHost.style.transition = 'height 340ms cubic-bezier(0.22, 1, 0.36, 1)';
        panelHost.getBoundingClientRect();

        window.requestAnimationFrame(() => {
            panelHost.style.height = `${endHeight}px`;
        });

        this.heightTimer = window.setTimeout(() => {
            panelHost.style.removeProperty('height');
            panelHost.style.removeProperty('overflow');
            panelHost.style.removeProperty('transition');
        }, PANEL_HEIGHT_DELAY);
    }
}

if (!customElements.get('tabs-component')) {
    customElements.define('tabs-component', TabsComponent);
}
